import {
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  where
} from 'firebase/firestore'
import { applyFactionMemberRoleToPoints } from './factionScoring'
import { getFactionStoredWrestlers } from './factionWrestlers'
import { getFactionMemberRoleAtDateFromLineup } from './factionLineupRole'
import { hasScoredParticipations, toShowDate } from './showLiveWindow'

const toFinitePoints = value => {
  const points = Number(value)
  return Number.isFinite(points) ? points : 0
}

const LAST_SHOW_MESSAGE_RANGES = [
  {
    min: Number.NEGATIVE_INFINITY,
    max: 0,
    messages: [
      'Serata storta, serve una reazione.',
      'Non si è acceso quasi nulla.',
      'Questa volta è andata in salita.',
      'La tua fazione ha jobbatto contro tutti.',
      'Nemmeno il VAR avrebbe trovato dei punti.',
      'Più che una fazione sembrava una fila alla posta.',
      'Hai scelto il main eventer... di Main Event.',
      'Stanotte hanno vinto tutti, tranne la tua fazione.',
      'Il booker oggi non era dalla tua parte.',
      'Performance degna di un roll-up al primo minuto.',
      'Hai preso più schienamenti che bonus.',
      'La tua fazione è entrata... e il pubblico era già al parcheggio.',
      'Più sconfitte che applausi.',
      'La tua fazione ha subito un vero squash match.',
      'La classifica oggi ti ha fatto un heel turn.'
    ]
  },
  {
    min: 1,
    max: 5,
    messages: [
      'Potevi fare meglio!',
      'Non ti è andata male, ma neanche bene!',
      'Qualcosina si è visto, ma serve di più.',
      'Serata timida per la tua fazione.',
      'Piccoli passi, senza strappi.',
      'Qualche spot c\'è stato, ma niente "This is Awesome!".',
      'La cintura è ancora molto lontana.',
      'La tua fazione ha bisogno di un nuovo piano.',
      'Hai preso qualche punto come chi ruba una vittoria con un roll-up.',
      'Il pubblico aspettava il comeback... anche noi.',
      'Non è stata una sconfitta, è stato un segmento di transizione.',
      'Hai acceso il ring... con una candela.',
      'Il momentum è ancora nel backstage.',
      "Qualche punto salvato all'ultimo secondo.",
      'Il tuo roster oggi ha fatto presenza.'
    ]
  },
  {
    min: 6,
    max: 15,
    messages: [
      'Hai tenuto botta, ma puoi salire ancora.',
      'Prestazione discreta, manca il colpo grosso.',
      'Buona base, ora serve continuità.',
      'Serata solida, non ancora memorabile.',
      'Hai mosso la classifica nel modo giusto.',
      "Hai portato a casa la vittoria, ma senza fuochi d'artificio.",
      'La tua fazione ha fatto il suo lavoro.',
      'Stai costruendo una buona storyline.',
      'Hai trovato qualche punto nascosto sotto il ring.',
      'Il momentum inizia a girare dalla tua parte.',
      'Non hai rubato lo show... ma almeno eri nello show.',
      'Il pubblico applaude... senza alzarsi in piedi.',
      'Un passo alla volta verso il titolo.',
      'La tua fazione sta costruendo qualcosa di interessante.',
      'Hai trovato la giusta chimica tra i wrestler.',
      'Il tuo piano sta iniziando a funzionare.'
    ]
  },
  {
    min: 16,
    max: 35,
    messages: [
      'Bella serata, qui si ragiona sul serio.',
      'Hai fatto sentire la tua presenza.',
      'Prestazione convincente e concreta.',
      'La tua fazione ha risposto bene.',
      'Punti pesanti, continua così.',
      'Questa sì che è una fazione che fa paura.',
      "Hai mandato un messaggio chiaro all'intero roster.",
      'Il tuo booker oggi ha azzeccato tutto.',
      'Hai fatto il promo e poi hai mantenuto la promessa.',
      'Prestazione da contender credibile.',
      'La tua fazione ha dominato il centro del ring.',
      'Gli altri GM stanno già studiando le tue scelte.',
      'Hai trovato la formula giusta.',
      'Oggi il pubblico cantava il nome della tua fazione.',
      'Hai scelto i wrestler giusti al momento giusto.',
      'Il tuo roster oggi sembrava costruito per vincere.',
      'La concorrenza inizia a guardarsi le spalle.'
    ]
  },
  {
    min: 36,
    max: 50,
    messages: [
      'Show di alto livello, davvero tanta roba.',
      'Serata quasi perfetta.',
      'Hai spinto forte e si vede.',
      'Prestazione da fascia alta.',
      'Qui il ritmo è stato importante.',
      'Hai rubato lo show.',
      'La tua fazione ha combattuto come se fosse WrestleMania.',
      'Il match della serata era il tuo.',
      'Oggi sembravi avere lo script in anticipo.',
      'Hai distribuito finisher come fossero volantini.',
      'La tua fazione ha chiuso ogni discussione.',
      'Prestazione da Hall of Fame.',
      'Hai fatto sembrare facile una serata difficile.',
      'Il pubblico è ancora in piedi ad applaudire.',
      'I tuoi wrestler hanno trasformato i bonus in arte.',
      'Hai messo tutti gli avversari alle corde.',
      'Questa è una prestazione da main event.',
      'Hai dominato senza bisogno di interferenze.'
    ]
  },
  {
    min: 51,
    max: Number.POSITIVE_INFINITY,
    messages: [
      'Dominio totale!',
      'Hai demolito lo show!',
      'Prestazione mostruosa!',
      'Serata da ricordare!',
      'Qui hai fatto paura a tutti.',
      'Hai squashato tutto il roster.',
      'La tua fazione è entrata in God Mode.',
      'Gli avversari stanno già cercando nuovi wrestler.',
      'Il GM avversario ha chiesto il rematch... per pietà.',
      'Hai preso più punti che chop in un match di Gunther.',
      'Hai fatto talmente tanti punti che il tabellone ha chiesto una pausa.',
      'Il resto del roster sta controllando se hai barato.',
      'Questa fazione va studiata nei libri di storia.',
      'Hai trasformato lo show in un allenamento.',
      'La tua fazione oggi ha deciso che perdere era fuori discussione.',
      'Hai fatto il cash-in sui punti.',
      'Sei uscito dal PLE con tutte le cinture... e pure quelle degli altri.',
      'La tua fazione ha fatto un record da Guinness.',
      'Il titolo dovrebbe essere rinominato con il tuo nome.',
      'I tuoi avversari vogliono cambiare gioco.',
      'Hai giocato come se conoscessi già il copione.',
      'Oggi hai scritto la storia del FantaWrestling.',
      'BREAKING NEWS: la tua fazione ha rotto il sistema dei punteggi.',
      'La WWE sta valutando di introdurre un salary cap dopo questa prestazione.',
      'Triple H vuole sapere chi è il tuo scout.',
      'Il roster è in riunione per capire come fermarti.',
      'La tua fazione è diventata il nuovo Tribal Chief della classifica.',
      'Lo staff FantaWrestling ha controllato due volte perché sembrava impossibile.',
      'Hai trovato il cheat code del FantaWrestling.',
      'La tua asta iniziale dovrebbe essere studiata nelle università.',
      'Gli altri manager stanno chiedendo una pausa pubblicitaria.',
      'La tua fazione ha fatto un turn heel sulla classifica.',
      'La tua fazione è entrata nella Hall of Fame del FantaWrestling.',
      'Nemmeno il booking di Vince Russo avrebbe creato una serata così folle.'
    ]
  }
]

const hashString = value =>
  String(value || '')
    .split('')
    .reduce((acc, char) => {
      return (acc * 31 + char.charCodeAt(0)) >>> 0
    }, 7)

const LAST_SHOW_CONTRIBUTOR_ROLE_META = {
  Main_Eventer: {
    label: 'Main Eventer',
    toneClassName: 'is-main-eventer'
  },
  In_Ring: {
    label: 'In-Ring',
    toneClassName: 'is-in-ring'
  },
  Backstage: {
    label: 'Backstage',
    toneClassName: 'is-backstage'
  }
}

const LAST_SHOW_CONTRIBUTOR_BONUS_META = {
  Main_Eventer: {
    label: 'x2',
    toneClassName: 'is-main-eventer'
  },
  Backstage: {
    label: 'x0',
    toneClassName: 'is-backstage'
  }
}

const formatShowDisplayDate = showDate =>
  showDate ? showDate.toLocaleDateString('it-IT') : ''

const buildShowInfo = (showId, showData = {}) => {
  const showDate = toShowDate(showData?.date || showData?.data || showData)

  return {
    id: showId || null,
    nome: showData?.nome || showData?.name || '',
    logo: showData?.logo || '',
    sfondo: showData?.sfondo || '',
    type: showData?.type || 'weekly',
    data:
      typeof showData?.data === 'string' && showData.data
        ? showData.data
        : formatShowDisplayDate(showDate),
    date: showDate
  }
}

const buildParticipationEntry = docSnap => ({
  id: docSnap.id,
  ...docSnap.data()
})

const getShowDayRange = value => {
  const showDate = toShowDate(value)
  if (!showDate) return null

  const start = new Date(
    showDate.getFullYear(),
    showDate.getMonth(),
    showDate.getDate()
  )
  const end = new Date(start)
  end.setDate(end.getDate() + 1)

  return { start, end }
}

const getContributorCountedPoints = contributor =>
  toFinitePoints(contributor?.countedPoints ?? contributor?.points)

const sortContributorsByPoints = (contributors = []) =>
  [...contributors].sort(
    (a, b) =>
      getContributorCountedPoints(b) - getContributorCountedPoints(a) ||
      toFinitePoints(b?.points) - toFinitePoints(a?.points) ||
      a.name.localeCompare(b.name, 'it')
  )

export const getLastShowContributorRoleMeta = status =>
  LAST_SHOW_CONTRIBUTOR_ROLE_META[status] || null

export const getLastShowContributorBonusMeta = status =>
  LAST_SHOW_CONTRIBUTOR_BONUS_META[status] || null

export const formatSignedPoints = value => {
  const points = toFinitePoints(value)
  return `${points > 0 ? '+' : ''}${points}`
}

const getLineupRoleMeta = role => {
  if (role === 'Main_Eventer') {
    return {
      role,
      roleLabel: 'Main Eventer',
      roleIcon: 'bi-star-fill',
      multiplierLabel: 'x2'
    }
  }

  if (role === 'Backstage') {
    return {
      role,
      roleLabel: 'Backstage',
      roleIcon: 'bi-door-closed',
      multiplierLabel: 'x0'
    }
  }

  if (role === 'In_Ring') {
    return {
      role,
      roleLabel: 'In-Ring',
      roleIcon: 'bi-person-fill',
      multiplierLabel: 'x1'
    }
  }

  return {
    role: role || null,
    roleLabel: '',
    roleIcon: '',
    multiplierLabel: ''
  }
}

export const getLastShowBannerMessage = ({
  totalPoints = 0,
  seed = ''
} = {}) => {
  const range =
    LAST_SHOW_MESSAGE_RANGES.find(
      item => totalPoints >= item.min && totalPoints <= item.max
    ) || LAST_SHOW_MESSAGE_RANGES[0]

  const options = range.messages || []
  if (options.length === 0) return ''

  const index = hashString(`${seed}:${totalPoints}`) % options.length
  return options[index]
}

export const findLatestScoredShowEntry = async ({
  db,
  referenceDate = new Date(),
  candidateLimit = 20
}) => {
  const now = toShowDate(referenceDate) || new Date()
  const showsSnap = await getDocs(
    query(
      collection(db, 'shows'),
      where('data', '<', now),
      orderBy('data', 'desc'),
      limit(candidateLimit)
    )
  )

  if (showsSnap.empty) {
    return null
  }

  const candidateDocs = showsSnap.docs
  const participantSnaps = await Promise.all(
    candidateDocs.map(docSnap =>
      getDocs(collection(db, 'shows', docSnap.id, 'participations'))
    )
  )

  const selectedIndex = participantSnaps.findIndex(snap =>
    hasScoredParticipations(snap.docs.map(docSnap => docSnap.data()))
  )

  if (selectedIndex === -1) {
    return null
  }

  const showDoc = candidateDocs[selectedIndex]
  const showData = showDoc.data()
  const showDate = toShowDate(showData.data)
  const selectedParticipations = participantSnaps[selectedIndex].docs.map(
    buildParticipationEntry
  )
  const show = buildShowInfo(showDoc.id, showData)
  let showEntries = [
    {
      show,
      participations: selectedParticipations
    }
  ]

  const showDayRange = getShowDayRange(showDate)
  if (showDayRange) {
    const sameDayShowsSnap = await getDocs(
      query(
        collection(db, 'shows'),
        where('data', '>=', showDayRange.start),
        where('data', '<', showDayRange.end),
        orderBy('data', 'desc')
      )
    )

    if (!sameDayShowsSnap.empty) {
      const sameDayParticipantSnaps = await Promise.all(
        sameDayShowsSnap.docs.map(docSnap =>
          getDocs(collection(db, 'shows', docSnap.id, 'participations'))
        )
      )

      const scoredSameDayEntries = sameDayShowsSnap.docs
        .map((docSnap, index) => ({
          show: buildShowInfo(docSnap.id, docSnap.data()),
          participations: sameDayParticipantSnaps[index].docs.map(
            buildParticipationEntry
          )
        }))
        .filter(entry => hasScoredParticipations(entry.participations))

      if (scoredSameDayEntries.length > 0) {
        showEntries = scoredSameDayEntries
      }
    }
  }

  return {
    show,
    participations: selectedParticipations,
    showEntries
  }
}

export const buildFactionLastShowSummary = ({
  faction,
  members = [],
  participations = [],
  allWrestlers = [],
  show = null,
  showEntries = []
}) => {
  if (!faction) return null

  const sourceMembers =
    Array.isArray(members) && members.length > 0
      ? members
      : getFactionStoredWrestlers(faction)

  const memberIds = sourceMembers
    .map(member => member?.id || member?.wrestlerId)
    .filter(Boolean)

  if (memberIds.length === 0) {
    return {
      showId: show?.id || null,
      showName: show?.nome || '',
      showDate: show?.data || '',
      totalPoints: 0,
      participantCount: 0,
      contributors: [],
      topContributors: [],
      hasContributors: false,
      showSummaries: [],
      showCount: 0,
      hasMultipleShows: false
    }
  }

  const memberIdSet = new Set(memberIds)
  const wrestlerNamesById = {}

  sourceMembers.forEach(member => {
    const memberId = member?.id || member?.wrestlerId
    if (!memberId) return
    wrestlerNamesById[memberId] =
      member?.name || member?.nome || wrestlerNamesById[memberId] || 'Wrestler'
  })

  allWrestlers.forEach(wrestler => {
    if (!wrestler?.id) return
    wrestlerNamesById[wrestler.id] =
      wrestler?.name ||
      wrestler?.nome ||
      wrestlerNamesById[wrestler.id] ||
      'Wrestler'
  })

  const sourceShowEntries =
    Array.isArray(showEntries) && showEntries.length > 0
      ? showEntries
      : [
          {
            show,
            participations
          }
        ]

  const buildContributor = (participation, showEntry) => {
    const wrestlerId = participation?.wrestlerId
    const rawPoints = toFinitePoints(participation?.totalPoints ?? participation?.points)
    const status = getFactionMemberRoleAtDateFromLineup(
      faction,
      wrestlerId,
      showEntry?.show?.date || showEntry?.show?.data || show
    )
    const roleMeta = getLineupRoleMeta(status)

    return {
      id: wrestlerId,
      name: wrestlerNamesById[wrestlerId] || 'Wrestler',
      points: rawPoints,
      countedPoints: applyFactionMemberRoleToPoints(rawPoints, status),
      status: status || null,
      roleLabel: roleMeta.roleLabel
    }
  }

  const showSummaries = sourceShowEntries
    .map(showEntry => {
      const showContributors = (showEntry.participations || [])
        .filter(participation => memberIdSet.has(participation?.wrestlerId))
        .map(participation => buildContributor(participation, showEntry))
        .filter(entry => entry.points !== 0 || entry.countedPoints !== 0)
        .sort(
          (a, b) =>
            getContributorCountedPoints(b) - getContributorCountedPoints(a) ||
            toFinitePoints(b.points) - toFinitePoints(a.points) ||
            a.name.localeCompare(b.name, 'it')
        )

      return {
        showId: showEntry?.show?.id || null,
        showName: showEntry?.show?.nome || showEntry?.show?.name || '',
        showDate: showEntry?.show?.data || '',
        totalPoints: showContributors.reduce(
          (sum, entry) => sum + getContributorCountedPoints(entry),
          0
        ),
        participantCount: showContributors.length,
        contributors: showContributors,
        hasContributors: showContributors.length > 0
      }
    })
    .filter(summary => summary.showId || summary.contributors.length > 0)

  const topContributors = sortContributorsByPoints(
    showSummaries.flatMap(summary =>
      summary.contributors.map(contributor => ({
        ...contributor,
        showId: summary.showId,
        showName: summary.showName
      }))
    )
  ).slice(0, 3)

  return {
    showId: show?.id || null,
    showName: show?.nome || '',
    showDate: show?.data || '',
    totalPoints: showSummaries.reduce(
      (sum, summary) => sum + summary.totalPoints,
      0
    ),
    participantCount: showSummaries.reduce(
      (sum, summary) => sum + summary.participantCount,
      0
    ),
    contributors: topContributors,
    topContributors,
    hasContributors: topContributors.length > 0,
    showSummaries,
    showCount: showSummaries.length,
    hasMultipleShows: showSummaries.length > 1
  }
}
